import axios from 'axios';
import type { Task } from '../types';

// Production: use same origin (/api) so login works when frontend is on e.g. https://projects.nstargrp.com
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:3001/api';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Let browser set multipart boundary for uploads
  if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
    delete (config.headers as Record<string, unknown>)['Content-Type'];
  }
  return config;
});

// Handle 401: clear token and redirect to login (caller can still catch and show toast)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
  verifyEmail: (token: string) =>
    api.get('/auth/verify-email', { params: { token } }),
  resendVerification: (email: string) =>
    api.post('/auth/resend-verification', { email }),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (name: string, email: string, password: string, role: 'masterAdmin' | 'admin' | 'endUser', organizations?: { organizationId: string; role: 'admin' | 'member' }[]) =>
    api.post('/users', { name, email, password, role, organizations }),
  update: (id: string, data: { name?: string; email?: string; password?: string; role?: 'masterAdmin' | 'admin' | 'endUser'; organizations?: { organizationId: string; role: 'admin' | 'member' }[] }) =>
    api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  sendResetLink: (userId: string) => api.post(`/users/${userId}/send-reset-link`),
  sendVerificationLink: (userId: string) => api.post(`/users/${userId}/send-verification-link`),
  uploadMyAvatar: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/users/me/avatar', fd, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  },
};

export const organizationsAPI = {
  getAll: (includeAll?: boolean) => api.get(`/organizations${includeAll ? '?includeAll=true' : ''}`),
  getById: (id: string) => api.get(`/organizations/${id}`),
  getMembers: (id: string) => api.get(`/organizations/${id}/members`),
  create: (name: string, description?: string) =>
    api.post('/organizations', { name, description }),
  uploadLogo: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/organizations/${id}/logo`, fd, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  },
  delete: (id: string) => api.delete(`/organizations/${id}`),
};

export const projectsAPI = {
  getByOrganization: (orgId: string, includeArchived?: boolean) => 
    api.get(`/projects/organization/${orgId}${includeArchived ? '?includeArchived=true' : ''}`),
  getById: (id: string) => api.get(`/projects/${id}`),
  create: (data: any) => api.post('/projects', data),
  update: (id: string, data: any) => api.put(`/projects/${id}`, data),
  updateDates: (id: string, data: { startDate?: string | null; endDate?: string | null; reason: string }) =>
    api.patch(`/projects/${id}/dates`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  archive: (id: string, archived: boolean) => 
    api.patch(`/projects/${id}/archive`, { archived }),
  addColumn: (projectId: string, title: string, color?: string, setAsFinished?: boolean) =>
    api.post(`/projects/${projectId}/columns`, { title, color, setAsFinished }),
  setFinishedColumn: (projectId: string, columnId: string | null) =>
    api.patch(`/projects/${projectId}/finished-column`, { columnId }),
  reorderColumns: (projectId: string, columnIds: string[]) =>
    api.patch(`/projects/${projectId}/columns/reorder`, { columnIds }),
  deleteColumn: (projectId: string, columnId: string) =>
    api.delete(`/projects/${projectId}/columns/${columnId}`),
  addMember: (projectId: string, userId: string) =>
    api.post(`/projects/${projectId}/members`, { userId }),
  removeMember: (projectId: string, userId: string) =>
    api.delete(`/projects/${projectId}/members/${userId}`),
};

export const tasksAPI = {
  getByProject: (projectId: string) => api.get(`/tasks/project/${projectId}`),
  getById: (id: string) => api.get(`/tasks/${id}`),
  create: (data: any) => api.post('/tasks', data),
  update: (id: string, data: any) => api.put(`/tasks/${id}`, data),
  delete: (id: string) => api.delete(`/tasks/${id}`),
  updateStatus: (id: string, status: string, order: number) =>
    api.patch(`/tasks/${id}/status`, { status, order }),
  addComment: (taskId: string, text: string, file?: File) => {
    if (file) {
      const fd = new FormData();
      fd.append('text', text);
      fd.append('file', file);
      return api.post<Task>(`/tasks/${taskId}/comments`, fd, {
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      });
    }
    return api.post<Task>(`/tasks/${taskId}/comments`, { text });
  },
  updateComment: (taskId: string, commentId: string, text: string) =>
    api.put<Task>(`/tasks/${taskId}/comments/${commentId}`, { text }),
  deleteComment: (taskId: string, commentId: string) =>
    api.delete(`/tasks/${taskId}/comments/${commentId}`),
  uploadCommentAttachment: (taskId: string, commentId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<Task>(`/tasks/${taskId}/comments/${commentId}/attachments`, fd, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  },
  deleteCommentAttachment: (taskId: string, commentId: string, attachmentId: string) =>
    api.delete<Task>(`/tasks/${taskId}/comments/${commentId}/attachments/${attachmentId}`),
  downloadCommentAttachmentBlob: (taskId: string, commentId: string, attachmentId: string) =>
    api.get<Blob>(
      `/tasks/${taskId}/comments/${commentId}/attachments/${attachmentId}/file`,
      { responseType: 'blob' },
    ),
  uploadAttachment: (taskId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<Task>(`/tasks/${taskId}/attachments`, fd, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  },
  deleteAttachment: (taskId: string, attachmentId: string) =>
    api.delete<Task>(`/tasks/${taskId}/attachments/${attachmentId}`),
  downloadAttachmentBlob: (taskId: string, attachmentId: string) =>
    api.get<Blob>(`/tasks/${taskId}/attachments/${attachmentId}/file`, { responseType: 'blob' }),
};

export default api;

export const activityAPI = {
  getProject: (projectId: string) => api.get(`/activity/projects/${projectId}`),
  acknowledgeProject: (projectId: string) => api.put(`/activity/projects/${projectId}/acknowledge`),
  markTaskSeen: (taskId: string) => api.put(`/activity/tasks/${taskId}/seen`),
};

export interface MentionNotificationItem {
  _id: string;
  task: string;
  project: string;
  commentId: string;
  taskTitle: string;
  projectName: string;
  preview: string;
  readAt: string | null;
  createdAt: string;
  fromUser: { id: string; name: string; email?: string } | null;
}

export const notificationsAPI = {
  list: (params?: { unread?: boolean; limit?: number }) =>
    api.get<{ unreadCount: number; items: MentionNotificationItem[] }>('/notifications', {
      params: {
        unread: params?.unread ? 'true' : undefined,
        limit: params?.limit,
      },
    }),
  unreadCount: () => api.get<{ unreadCount: number }>('/notifications/unread-count'),
  markRead: (id: string) =>
    api.put<{ item: MentionNotificationItem; unreadCount: number }>(`/notifications/${id}/read`),
  markAllRead: () => api.put<{ unreadCount: number }>('/notifications/read-all'),
};
